/**
 * @voltxampere/web — Keyboard shortcut manager (Sprint 11).
 *
 * Single source of truth for keyboard dispatch across the
 * shell. Components do *not* attach their own `keydown`
 * listeners; they register here and the manager fans events
 * out by canonical key + active scope.
 *
 * Canonical key format: pieces joined by `+`, in this order:
 *   `Cmd` | `Alt` | `Shift` | <key>
 *
 * `Cmd` is unified across `metaKey` (macOS) and `ctrlKey`
 * (Windows / Linux) so call sites can register one accelerator
 * and have it work everywhere. `Shift` is omitted for printable
 * non-alpha keys (e.g. `?`, `+`) because the typed character
 * already encodes the modifier; otherwise letter shortcuts
 * become ambiguous when typed under caps lock.
 *
 * Sprint 17 will expand the scope set (canvas / inspector /
 * waveform). Sprint 11 supports `global` (default), `modal`
 * (set when a modal is open), and `any` (always fires).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

export type Scope = "any" | "global" | "modal" | "sidebar" | "canvas";

export type ShortcutCategory = "global" | "symbol";

export type ShortcutHandler = (e: KeyboardEvent) => void;

export interface Shortcut {
  /** Canonical key combo, e.g. "Cmd+K", "Shift+P", "?", "Escape". */
  readonly key: string;
  readonly scope: Scope;
  readonly handler: ShortcutHandler;
  /** Lazy locale-aware description for the `?` overlay. */
  readonly describe: () => string;
  readonly category?: ShortcutCategory;
}

export class ShortcutManager {
  private readonly shortcuts = new Map<string, Shortcut>();
  private activeScope: Scope = "global";

  register(shortcut: Shortcut): void {
    if (this.shortcuts.has(shortcut.key)) {
      // Last-wins is fine for Sprint 11; surface a warning so
      // duplicate registrations are visible during development.
      console.warn(`[ShortcutManager] Replacing existing shortcut "${shortcut.key}".`);
    }
    this.shortcuts.set(shortcut.key, shortcut);
  }

  unregister(key: string): void {
    this.shortcuts.delete(key);
  }

  clear(): void {
    this.shortcuts.clear();
    this.activeScope = "global";
  }

  setScope(scope: Scope): void {
    this.activeScope = scope;
  }

  getScope(): Scope {
    return this.activeScope;
  }

  list(): readonly Shortcut[] {
    return [...this.shortcuts.values()];
  }

  has(key: string): boolean {
    return this.shortcuts.has(key);
  }

  handleKeyDown(e: KeyboardEvent): void {
    // Editable-target guard (Sprint 11.0.1). When focus is on an
    // <input>, <textarea>, <select>, or [contenteditable] node,
    // bare single-key shortcuts (R, ?, …) must not steal the
    // character. Modifier-bearing accelerators (Cmd+K) and
    // Escape (modal close) keep firing so power-user shortcuts
    // remain reachable from inside text fields.
    if (this.isEditableTarget(e)) {
      const hasModifier = e.metaKey || e.ctrlKey || e.altKey;
      const isEscape = e.key === "Escape";
      if (!hasModifier && !isEscape) return;
    }

    const combo = this.normalizeKey(e);
    const sc = this.shortcuts.get(combo);
    if (!sc) {
      return;
    }

    if (sc.scope !== "any" && sc.scope !== this.activeScope) {
      return;
    }

    e.preventDefault();
    sc.handler(e);
  }

  /**
   * True when an editable element is on the event's propagation
   * path. Uses `composedPath()` so retargeted shadow-DOM events
   * (a window listener sees `e.target` as the outermost custom
   * element, not the focused inner `<input>`) still resolve to
   * the real editable node. Falls back to `e.target` when the
   * event has no path — synthetic `KeyboardEvent`s constructed
   * in tests have an empty `composedPath()` per spec.
   */
  private isEditableTarget(e: KeyboardEvent): boolean {
    const path = e.composedPath();
    const candidates: readonly EventTarget[] =
      path.length > 0 ? path : e.target !== null ? [e.target] : [];
    for (const node of candidates) {
      if (!(node instanceof HTMLElement)) continue;
      const tag = node.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (node.isContentEditable) return true;
      // happy-dom (and some legacy browsers) skip updating the
      // `isContentEditable` getter when only the attribute is
      // set. Treat an explicit `contenteditable="true"` attribute
      // as the same signal.
      if (node.getAttribute("contenteditable") === "true") return true;
    }
    return false;
  }

  /**
   * Convert a `KeyboardEvent` to the canonical key string used
   * as a Map key. See file header for the contract.
   */
  normalizeKey(e: KeyboardEvent): string {
    const parts: string[] = [];

    if (e.metaKey || e.ctrlKey) parts.push("Cmd");
    if (e.altKey) parts.push("Alt");

    let k = e.key;
    if (k.length === 1 && /[a-zA-Z]/.test(k)) {
      // Letter — normalise to upper-case and add Shift explicitly.
      if (e.shiftKey) parts.push("Shift");
      k = k.toUpperCase();
    } else if (e.shiftKey && k.length > 1) {
      // Special key under shift (Shift+Tab, Shift+Enter, …).
      parts.push("Shift");
    }
    // Printable non-alpha (`?`, `+`, `:`) — char already encodes
    // any required shift, so do NOT add Shift to the canonical.

    parts.push(k);
    return parts.join("+");
  }
}

/**
 * Convert a symbol's display shortcut (e.g. "⇧P", "·", "R") to
 * the canonical key string used by `register`. The display
 * format is meant for badges; this helper bridges to the runtime
 * dispatch format.
 */
export function parseSymbolShortcut(display: string): string {
  const parts: string[] = [];
  let rest = display;

  if (rest.startsWith("⌘")) {
    parts.push("Cmd");
    rest = rest.slice(1);
  }
  if (rest.startsWith("⌥")) {
    parts.push("Alt");
    rest = rest.slice(1);
  }
  if (rest.startsWith("⇧")) {
    parts.push("Shift");
    rest = rest.slice(1);
  }

  if (rest.length === 1 && /[a-zA-Z]/.test(rest)) {
    rest = rest.toUpperCase();
  }

  parts.push(rest);
  return parts.join("+");
}

/** Singleton — shell components share one manager. */
export const shortcuts = new ShortcutManager();
